import { Component, effect, inject, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router, RouterLink } from '@angular/router'
import { AuthService } from '../../infrastructure/auth/auth.service'
import { supabaseConfigurado } from '../../infrastructure/supabase/cliente'
import { entraConfigurado } from '../../infrastructure/entra/cliente'
import { JamarLogoComponent } from '../components/jamar-logo'

/** Gestionamos el ingreso y redirigimos cuando ya existe una sesion activa. */
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [FormsModule, JamarLogoComponent, RouterLink],
  templateUrl: './login.html',
  styleUrl: './login.scss',
})
export class LoginComponent {
  private readonly auth = inject(AuthService)
  private readonly router = inject(Router)

  readonly supabaseConfigurado = supabaseConfigurado
  readonly entraConfigurado = entraConfigurado
  readonly email = signal('')
  readonly password = signal('')
  readonly error = signal<string | null>(null)
  readonly cargando = signal(false)
  readonly cargandoMicrosoft = signal(false)
  readonly mostrarPassword = signal(false)
  /** Puramente visual: Supabase ya persiste la sesion siempre, este checkbox no cambia ese comportamiento. */
  readonly recordarme = signal(true)

  constructor() {
    // Redirigimos tambien cuando la app abre con sesion persistida.
    effect(() => {
      if (this.auth.perfil()) void this.router.navigateByUrl('/', { replaceUrl: true })
    })
    // Con loginRedirect, un error de Microsoft se detecta al VOLVER (en
    // inicializar()), no en el click que lo disparo — se reusa el mismo
    // mensaje de error de la pantalla.
    effect(() => {
      const err = this.auth.errorMicrosoft()
      if (err) this.error.set(this.mensajeLegible(err))
    })
  }

  async enviar(e: Event): Promise<void> {
    e.preventDefault()
    this.cargando.set(true)
    this.error.set(null)
    try {
      const err = await this.auth.iniciarSesion(this.email(), this.password())
      this.error.set(this.mensajeLegible(err))
      // La navegacion ocurre cuando `perfil` se actualiza.
    } catch (err) {
      // Evitamos que el boton quede en estado de carga si falla la red.
      this.error.set(this.mensajeLegible(err instanceof Error ? err.message : null))
    } finally {
      this.cargando.set(false)
    }
  }

  async entrarConMicrosoft(): Promise<void> {
    this.cargandoMicrosoft.set(true)
    this.error.set(null)
    try {
      const err = await this.auth.entrarConMicrosoft()
      this.error.set(this.mensajeLegible(err))
      // La navegacion ocurre cuando `perfil` se actualiza (mismo efecto del constructor).
    } catch (err) {
      this.error.set(this.mensajeLegible(err instanceof Error ? err.message : null))
    } finally {
      this.cargandoMicrosoft.set(false)
    }
  }

  /** Ultimo resguardo: si llega un mensaje vacio o no legible (p.ej. "{}"), no lo mostramos tal cual. */
  private mensajeLegible(mensaje: string | null): string | null {
    if (!mensaje) return null
    const texto = mensaje.trim()
    if (!texto || texto === '{}' || texto === '[object Object]') {
      return 'No se pudo conectar. Intenta de nuevo.'
    }
    return texto
  }
}
